// FILE: src/components/ExplanationPanel.js
// Shown after the user submits an answer.
//
// Behaviour is controlled by the `mode` prop:
//
//   mode === 'practice'  (default)
//     Shows correct / incorrect status, the correct answer(s), and the full
//     explanation.  Identical to the original learning-feedback experience.
//
//   mode === 'full'  (simulation / full exam)
//     Shows only a confirmation that the answer was recorded.  Does NOT reveal
//     whether the answer is correct, does NOT show the correct answer, and does
//     NOT show the explanation.  This emulates the real certification exam
//     environment where feedback is withheld until the session is complete.
//
// In both modes the panel renders a "Next Question →" button that fires onNext.

import React from 'react';
import css from './ExplanationPanel.module.css';

// ── Shared styles ─────────────────────────────────────────────────────────────

const styles = {
  // ── Practice mode ──────────────────────────────────────────────────────────
  practicePanel: (isCorrect) => ({
    maxWidth: '720px',
    margin: '16px auto 0',
    padding: '24px 32px',
    borderRadius: '8px',
    border: `2px solid ${isCorrect ? '#2e7d32' : '#c62828'}`,
    background: isCorrect ? '#f1f8f1' : '#fff8f8',
  }),
  statusBadge: (isCorrect) => ({
    display: 'inline-block',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    background: isCorrect ? '#2e7d32' : '#c62828',
    color: '#fff',
    marginBottom: '16px',
  }),
  correctAnswerLabel: {
    fontSize: '0.85rem',
    color: '#555',
    marginBottom: '4px',
    fontWeight: '600',
  },
  correctAnswerValue: {
    fontSize: '1rem',
    marginBottom: '16px',
    color: '#222',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #ddd',
    margin: '16px 0',
  },
  explanationLabel: {
    fontSize: '0.85rem',
    color: '#555',
    fontWeight: '600',
    marginBottom: '6px',
  },
  explanationText: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: '#333',
  },

  // ── Simulation mode ────────────────────────────────────────────────────────
  simulationPanel: {
    maxWidth: '720px',
    margin: '16px auto 0',
    padding: '24px 32px',
    borderRadius: '8px',
    border: '2px solid #333',
    background: '#f8f8f8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '10px',
  },
  recordedBadge: {
    display: 'inline-block',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    background: '#333',
    color: '#fff',
  },
  recordedNote: {
    fontSize: '0.9rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },

  // ── Shared ─────────────────────────────────────────────────────────────────
  nextRow: {
    marginTop: '24px',
    textAlign: 'right',
    width: '100%',
  },
  nextButton: {
    padding: '12px 32px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCorrectAnswerText(choices, correctAnswers) {
  return correctAnswers
    .map((id) => {
      const match = choices.find((c) => c.id === id);
      return match ? `${match.id}. ${match.text}` : id;
    })
    .join(' | ');
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ExplanationPanel({
  question,
  selectedAnswers,
  onNext,
  mode = 'practice',
}) {
  if (!question) return null;

  // ── Simulation / Full Exam mode ───────────────────────────────────────────
  //
  // Do NOT reveal:
  //   • whether the answer was correct or incorrect
  //   • the correct answer
  //   • the explanation
  //
  // DO show:
  //   • confirmation that the answer was recorded
  //   • "Next Question →" button

  if (mode === 'full') {
    return (
      <div style={styles.simulationPanel} className={css.panel} data-testid="simulation-feedback-panel">
        <div style={styles.recordedBadge}>✓ Answer Recorded</div>
        <p style={styles.recordedNote}>
          Your answer has been saved. Results and explanations will be available
          after you complete the exam.
        </p>
        <div style={styles.nextRow}>
          <button
            data-testid="next-question-button"
            style={styles.nextButton}
            onClick={onNext}
          >
            Next Question →
          </button>
        </div>
      </div>
    );
  }

  // ── Practice mode — full learning feedback ────────────────────────────────

  const { choices, correctAnswers, explanation } = question;

  const isCorrect =
    selectedAnswers.length === correctAnswers.length &&
    correctAnswers.every((id) => selectedAnswers.includes(id));

  const correctAnswerText = getCorrectAnswerText(choices, correctAnswers);

  return (
    <div style={styles.practicePanel(isCorrect)} className={css.panel} data-testid="explanation-panel">
      <div style={styles.statusBadge(isCorrect)}>
        {isCorrect ? '✓ Correct' : '✗ Incorrect'}
      </div>

      <div style={styles.correctAnswerLabel}>Correct Answer</div>
      <div style={styles.correctAnswerValue}>{correctAnswerText}</div>

      <hr style={styles.divider} />

      <div style={styles.explanationLabel}>Explanation</div>
      <div style={styles.explanationText} data-testid="explanation-text">
        {question.explanationDeferred === true
          ? 'Loading explanation…' // stage 3 PR 3: on-demand fetch is in flight
          : explanation}
      </div>

      <div style={styles.nextRow}>
        <button
          data-testid="next-question-button"
          style={styles.nextButton}
          onClick={onNext}
        >
          Next Question →
        </button>
      </div>
    </div>
  );
}
